import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  WorkoutLog,
  DifficultyRating,
  ExerciseLogStatus,
  SkipReason,
  createWorkoutLog,
  updateWorkoutLog,
  listWorkoutLogs,
  submitModuleFeedback,
  submitProof,
  listSubmissions,
  Module,
  DrillData,
  DrillSubmission,
} from '@tss/shared';
import { ChevronLeft, ChevronRight, X, Check, Minus, Plus, SkipForward, Timer, Trophy, Video, Upload, AlertCircle, Camera, Dumbbell } from 'lucide-react';
import { ProofUploadModal } from './ProofUploadModal';
import { Badge } from './UI/Badge';
import { Skeleton } from './UI/Skeleton';

// Skip reason options with labels
const SKIP_REASON_OPTIONS: { value: SkipReason; label: string; icon: string }[] = [
  { value: 'NO_EQUIPMENT', label: 'No equipment', icon: '🏋️' },
  { value: 'INJURY', label: 'Injury/pain', icon: '🤕' },
  { value: 'TOO_HARD', label: 'Too difficult', icon: '😰' },
  { value: 'NO_TIME', label: 'No time', icon: '⏰' },
  { value: 'FATIGUE', label: 'Too tired', icon: '😫' },
  { value: 'ALREADY_DID', label: 'Already did today', icon: '✅' },
  { value: 'NOT_UNDERSTOOD', label: "Don't understand", icon: '❓' },
  { value: 'OTHER', label: 'Other reason', icon: '📝' },
];

interface WorkoutTrackerProps {
  programId: string;
  module: Module;
  orgId: string;
  onComplete?: () => void;
  onClose?: () => void;
  proxyAthleteId?: string;  // For guardian proxy mode - log on behalf of managed athlete
}

interface DrillState {
  drill_index: number;
  drill_name: string;
  description?: string;
  category?: string;
  difficulty_level?: string;
  target_reps?: number;
  target_sets?: number;
  target_duration_seconds?: number;
  target_rest_seconds?: number;
  log?: WorkoutLog;
  completed_sets: number;
  reps_per_set: number;
  status: ExerciseLogStatus;
  // Proof tracking
  requires_proof: boolean;
  proof_submitted: boolean;
  submission?: DrillSubmission;
}

const DIFFICULTY_OPTIONS: { value: DifficultyRating; label: string; emoji: string }[] = [
  { value: 'TOO_EASY', label: 'Easy', emoji: '😴' },
  { value: 'JUST_RIGHT', label: 'Perfect', emoji: '👍' },
  { value: 'CHALLENGING', label: 'Hard', emoji: '💪' },
  { value: 'TOO_HARD', label: 'Too Hard', emoji: '😰' },
];

export const WorkoutTracker: React.FC<WorkoutTrackerProps> = ({
  programId,
  module,
  orgId,
  onComplete,
  onClose,
  proxyAthleteId,
}) => {
  const [drills, setDrills] = useState<DrillState[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Keep a ref to always have access to latest drills (avoids stale closures)
  const drillsRef = useRef<DrillState[]>([]);
  useEffect(() => {
    drillsRef.current = drills;
  }, [drills]);

  // Rest timer
  const [restSeconds, setRestSeconds] = useState<number | null>(null);
  const [isResting, setIsResting] = useState(false);
  const restIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Completion state
  const [showComplete, setShowComplete] = useState(false);
  const [difficulty, setDifficulty] = useState<DifficultyRating | null>(null);

  // Skip reason selection modal
  const [showSkipReasonModal, setShowSkipReasonModal] = useState(false);
  const [pendingSkipIndex, setPendingSkipIndex] = useState<number | null>(null);

  // Proof submission modal
  const [showProofModal, setShowProofModal] = useState(false);
  const [proofDrillIndex, setProofDrillIndex] = useState<number | null>(null);
  const [proofDrillName, setProofDrillName] = useState('');

  // Proof prompt after completing drill (integrated flow)
  const [showProofPrompt, setShowProofPrompt] = useState(false);
  const [pendingAdvanceDrills, setPendingAdvanceDrills] = useState<DrillState[] | null>(null);
  const [completedDrillForProof, setCompletedDrillForProof] = useState<DrillState | null>(null);

  // Parse drill data
  const parseDrill = (drill: unknown): DrillData | null => {
    if (typeof drill === 'string') {
      try { return JSON.parse(drill) as DrillData; }
      catch { return { name: drill }; }
    }
    return drill && typeof drill === 'object' ? drill as DrillData : null;
  };

  // Initialize
  useEffect(() => {
    const init = async () => {
      setLoading(true);
      try {
        // Fetch workout logs and submissions in parallel
        const [logsResult, submissionsResult] = await Promise.all([
          listWorkoutLogs(programId, { module_id: module.module_id, athlete_id: proxyAthleteId }),
          listSubmissions(programId, { module_id: module.module_id, athlete_id: proxyAthleteId }).catch(() => ({ submissions: [] }))
        ]);
        const { workout_logs } = logsResult;
        const submissions = submissionsResult.submissions || [];

        // Create a map of submissions by drill_index
        const submissionMap = new Map<number, DrillSubmission>();
        submissions.forEach((sub: DrillSubmission) => {
          submissionMap.set(sub.drill_index, sub);
        });

        const states: DrillState[] = module.drills.map((drill, index) => {
          const data = parseDrill(drill);
          const existingLog = workout_logs.find(l => l.drill_index === index);
          const existingSubmission = submissionMap.get(index);
          const targetSets = data?.sets || 1;
          const targetReps = data?.reps || 0;

          return {
            drill_index: index,
            drill_name: data?.name || 'Exercise',
            description: data?.description,
            category: data?.category,
            difficulty_level: data?.difficulty,
            target_reps: targetReps,
            target_sets: targetSets,
            target_duration_seconds: data?.duration_seconds,
            target_rest_seconds: data?.rest_seconds || 60,
            log: existingLog,
            completed_sets: existingLog?.actual_sets || 0,
            reps_per_set: existingLog?.actual_reps || targetReps || 10,
            status: existingLog?.status || 'NOT_STARTED',
            // Proof tracking - check both camelCase and snake_case
            requires_proof: data?.requires_proof || data?.requiresProof || false,
            proof_submitted: !!existingSubmission || existingLog?.proof_submitted || false,
            submission: existingSubmission,
          };
        });

        setDrills(states);

        // Start at first incomplete
        const firstIncomplete = states.findIndex(d => d.status !== 'COMPLETED' && d.status !== 'SKIPPED');
        setCurrentIndex(firstIncomplete >= 0 ? firstIncomplete : 0);
      } catch (err) {
        console.error('Failed to load:', err);
        setDrills(module.drills.map((drill, index) => {
          const data = parseDrill(drill);
          return {
            drill_index: index,
            drill_name: data?.name || 'Exercise',
            description: data?.description,
            category: data?.category,
            difficulty_level: data?.difficulty,
            target_reps: data?.reps || 0,
            target_sets: data?.sets || 1,
            target_duration_seconds: data?.duration_seconds,
            target_rest_seconds: data?.rest_seconds || 60,
            completed_sets: 0,
            reps_per_set: data?.reps || 10,
            status: 'NOT_STARTED',
            requires_proof: data?.requires_proof || data?.requiresProof || false,
            proof_submitted: false,
          };
        }));
      }
      setLoading(false);
    };
    init();
  }, [programId, module]);

  // Rest timer effect
  useEffect(() => {
    if (isResting && restSeconds !== null && restSeconds > 0) {
      restIntervalRef.current = setInterval(() => {
        setRestSeconds(prev => {
          if (prev === null || prev <= 1) {
            setIsResting(false);
            if ('vibrate' in navigator) navigator.vibrate([200, 100, 200, 100, 200]);
            return null;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => {
      if (restIntervalRef.current) clearInterval(restIntervalRef.current);
    };
  }, [isResting, restSeconds]);

  const skipRest = useCallback(() => {
    setIsResting(false);
    setRestSeconds(null);
    if (restIntervalRef.current) clearInterval(restIntervalRef.current);
  }, []);

  const startRest = useCallback((seconds: number) => {
    setRestSeconds(seconds);
    setIsResting(true);
  }, []);

  // Save drill to backend and update state - returns the new drills array
  const saveDrill = async (
    index: number,
    updates: { completed_sets: number; reps_per_set: number; status: ExerciseLogStatus }
  ): Promise<DrillState[]> => {
    // Use ref to get the LATEST drill state (avoids stale closure issues)
    const currentDrills = drillsRef.current;
    const drill = currentDrills[index];
    if (!drill) return currentDrills;

    setSaving(true);
    let newDrills = currentDrills;
    try {
      let log = drill.log;

      // Create log if it doesn't exist
      if (!log) {
        const { workout_log } = await createWorkoutLog(programId, {
          module_id: module.module_id,
          drill_index: drill.drill_index,
          drill_name: drill.drill_name,
          prescribed_reps: drill.target_reps,
          prescribed_sets: drill.target_sets,
          athlete_id: proxyAthleteId,  // For guardian proxy mode
        });
        log = workout_log;
      }

      // Update the log with new values
      await updateWorkoutLog(programId, log.log_id, {
        actual_sets: updates.completed_sets,
        actual_reps: updates.reps_per_set,
        status: updates.status,
        athlete_id: proxyAthleteId,  // For guardian proxy mode
      });

      // Use functional update to ensure we're working with latest state
      // and properly merge the log reference
      setDrills(prev => {
        newDrills = prev.map((d, i) =>
          i === index ? { ...d, log, ...updates } : d
        );
        return newDrills;
      });

      // Also update the ref immediately for subsequent calls
      drillsRef.current = newDrills;
    } catch (err) {
      console.error('Save failed:', err);
      // Return current drills on error
      newDrills = drillsRef.current;
    }
    setSaving(false);
    return newDrills;
  };

  // Complete a set
  const completeSet = async () => {
    // Use ref for latest state
    const drill = drillsRef.current[currentIndex];
    if (!drill) return;

    const newSets = drill.completed_sets + 1;
    const isLastSet = newSets >= (drill.target_sets || 1);
    const newStatus: ExerciseLogStatus = isLastSet ? 'COMPLETED' : 'IN_PROGRESS';

    // Save with new values - returns updated drills array
    const updatedDrills = await saveDrill(currentIndex, {
      completed_sets: newSets,
      reps_per_set: drill.reps_per_set,
      status: newStatus,
    });

    if (isLastSet) {
      // Check if this drill requires proof and hasn't been submitted yet
      const completedDrill = updatedDrills[currentIndex];
      if (completedDrill.requires_proof && !completedDrill.proof_submitted) {
        // Show proof prompt instead of advancing
        setCompletedDrillForProof(completedDrill);
        setPendingAdvanceDrills(updatedDrills);
        setShowProofPrompt(true);
      } else {
        advanceToNext(updatedDrills);
      }
    } else if (drill.target_rest_seconds) {
      startRest(drill.target_rest_seconds);
    }
  };

  // Skip current drill - show reason selection modal
  const skipDrill = () => {
    setPendingSkipIndex(currentIndex);
    setShowSkipReasonModal(true);
  };

  // Handle skip reason selection
  const handleSkipWithReason = async (reason: SkipReason) => {
    if (pendingSkipIndex === null) return;

    const drill = drillsRef.current[pendingSkipIndex];
    if (!drill) return;

    setShowSkipReasonModal(false);
    setSaving(true);

    try {
      let log = drill.log;

      // Create log if it doesn't exist
      if (!log) {
        const { workout_log } = await createWorkoutLog(programId, {
          module_id: module.module_id,
          drill_index: drill.drill_index,
          drill_name: drill.drill_name,
          prescribed_reps: drill.target_reps,
          prescribed_sets: drill.target_sets,
          athlete_id: proxyAthleteId,  // For guardian proxy mode
        });
        log = workout_log;
      }

      // Update the log with skip status and reason
      await updateWorkoutLog(programId, log.log_id, {
        actual_sets: drill.completed_sets,
        actual_reps: drill.reps_per_set,
        status: 'SKIPPED',
        skip_reason: reason,
        athlete_id: proxyAthleteId,  // For guardian proxy mode
      });

      // Update local state
      const newDrills = drillsRef.current.map((d, i) =>
        i === pendingSkipIndex ? { ...d, log, status: 'SKIPPED' as ExerciseLogStatus } : d
      );
      setDrills(newDrills);
      drillsRef.current = newDrills;

      advanceToNext(newDrills);
    } catch (err) {
      console.error('Skip failed:', err);
    }

    setSaving(false);
    setPendingSkipIndex(null);
  };

  // Open proof upload modal (from prompt flow)
  const openProofModalFromPrompt = () => {
    if (!completedDrillForProof) return;
    setProofDrillIndex(completedDrillForProof.drill_index);
    setProofDrillName(completedDrillForProof.drill_name);
    setShowProofModal(true);
  };

  // Handle proof submitted
  const handleProofSubmitted = () => {
    if (proofDrillIndex === null) return;

    // Update drill state to show proof was submitted
    const updatedDrills = drillsRef.current.map((d, i) =>
      i === proofDrillIndex ? { ...d, proof_submitted: true } : d
    );
    setDrills(updatedDrills);
    drillsRef.current = updatedDrills;

    setShowProofModal(false);
    setProofDrillIndex(null);
    setProofDrillName('');

    // If we were in the proof prompt flow, advance now
    if (showProofPrompt && pendingAdvanceDrills) {
      setShowProofPrompt(false);
      setCompletedDrillForProof(null);
      // Use updated drills with proof_submitted = true
      advanceToNext(updatedDrills);
      setPendingAdvanceDrills(null);
    }
  };

  // Skip proof for now and advance
  const skipProofAndAdvance = () => {
    if (!pendingAdvanceDrills) return;
    setShowProofPrompt(false);
    setCompletedDrillForProof(null);
    advanceToNext(pendingAdvanceDrills);
    setPendingAdvanceDrills(null);
  };

  // Advance to next incomplete - uses passed drills to avoid stale state
  const advanceToNext = (currentDrills: DrillState[]) => {
    const nextIncomplete = currentDrills.findIndex((d, i) =>
      i > currentIndex && d.status !== 'COMPLETED' && d.status !== 'SKIPPED'
    );

    if (nextIncomplete >= 0) {
      setCurrentIndex(nextIncomplete);
    } else {
      // Check if all done
      const allDone = currentDrills.every(d =>
        d.status === 'COMPLETED' || d.status === 'SKIPPED'
      );
      if (allDone) {
        setShowComplete(true);
      } else {
        // Find first incomplete
        const first = currentDrills.findIndex(d => d.status !== 'COMPLETED' && d.status !== 'SKIPPED');
        if (first >= 0) setCurrentIndex(first);
      }
    }
  };

  // Adjust reps
  const adjustReps = (delta: number) => {
    setDrills(prev => prev.map((d, i) =>
      i === currentIndex ? { ...d, reps_per_set: Math.max(1, d.reps_per_set + delta) } : d
    ));
  };

  // Submit final feedback
  const submitFeedback = async () => {
    if (!difficulty) return;

    setSaving(true);
    try {
      const completed = drills.filter(d => d.status === 'COMPLETED').length;
      const skipped = drills.filter(d => d.status === 'SKIPPED').length;

      await submitModuleFeedback(programId, {
        module_id: module.module_id,
        overall_difficulty: difficulty,
        exercises_completed: completed,
        exercises_skipped: skipped,
      });
      onComplete?.();
    } catch (err) {
      console.error('Feedback failed:', err);
      onComplete?.();
    }
  };

  // Progress calculation
  const completedCount = drills.filter(d => d.status === 'COMPLETED' || d.status === 'SKIPPED').length;
  const progress = drills.length > 0 ? Math.round((completedCount / drills.length) * 100) : 0;

  const currentDrill = drills[currentIndex];

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.loadingContainer}>
          {/* Header skeleton */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '16px', borderBottom: '1px solid var(--gray-700)' }}>
            <Skeleton width={32} height={32} borderRadius="50%" style={{ background: 'var(--gray-700)' }} />
            <Skeleton width="60%" height={8} borderRadius={4} style={{ background: 'var(--gray-700)' }} />
            <Skeleton width={40} height={16} borderRadius={4} style={{ background: 'var(--gray-700)' }} />
          </div>
          {/* Main content skeleton */}
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '24px', padding: '32px' }}>
            <div style={{ display: 'flex', gap: '8px' }}>
              <Skeleton width={80} height={24} borderRadius={12} style={{ background: 'var(--gray-700)' }} />
              <Skeleton width={60} height={24} borderRadius={12} style={{ background: 'var(--gray-700)' }} />
            </div>
            <Skeleton width="80%" height={32} borderRadius={8} style={{ background: 'var(--gray-700)' }} />
            <Skeleton width="50%" height={20} borderRadius={6} style={{ background: 'var(--gray-700)' }} />
            <div style={{ display: 'flex', gap: '8px', marginTop: '16px' }}>
              {[1, 2, 3].map(i => (
                <Skeleton key={i} width={12} height={12} borderRadius="50%" style={{ background: 'var(--gray-700)' }} />
              ))}
            </div>
            <Skeleton width={120} height={80} borderRadius={16} style={{ background: 'var(--gray-700)', marginTop: '24px' }} />
          </div>
          {/* Footer skeleton */}
          <div style={{ padding: '16px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <div style={{ display: 'flex', gap: '8px' }}>
              <Skeleton width="30%" height={44} borderRadius={10} style={{ background: 'var(--gray-700)' }} />
              <Skeleton width="30%" height={44} borderRadius={10} style={{ background: 'var(--gray-700)' }} />
              <Skeleton width="30%" height={44} borderRadius={10} style={{ background: 'var(--gray-700)' }} />
            </div>
            <Skeleton width="100%" height={56} borderRadius={14} style={{ background: 'var(--accent)', opacity: 0.3 }} />
          </div>
        </div>
      </div>
    );
  }

  // Completion screen
  if (showComplete) {
    return (
      <div style={styles.container}>
        <div style={styles.completeScreen}>
          <div style={styles.trophyCircle}>
            <Trophy size={48} color="#fbbf24" />
          </div>
          <h2 style={styles.completeTitle}>Workout Complete!</h2>
          <p style={styles.completeSubtitle}>
            {drills.filter(d => d.status === 'COMPLETED').length} of {drills.length} exercises done
          </p>

          <div style={styles.difficultySection}>
            <p style={styles.difficultyLabel}>How did it feel?</p>
            <div style={styles.difficultyGrid}>
              {DIFFICULTY_OPTIONS.map(opt => (
                <button
                  key={opt.value}
                  onClick={() => setDifficulty(opt.value)}
                  style={{
                    ...styles.difficultyBtn,
                    ...(difficulty === opt.value ? styles.difficultyBtnActive : {}),
                  }}
                >
                  <span style={styles.difficultyEmoji}>{opt.emoji}</span>
                  <span style={styles.difficultyText}>{opt.label}</span>
                </button>
              ))}
            </div>
          </div>

          <button
            onClick={submitFeedback}
            disabled={!difficulty || saving}
            style={{
              ...styles.finishBtn,
              opacity: !difficulty ? 0.5 : 1,
            }}
          >
            {saving ? 'Saving...' : 'Finish'}
          </button>
        </div>
      </div>
    );
  }

  // Rest timer overlay
  if (isResting && restSeconds !== null) {
    return (
      <div style={styles.container}>
        <div style={styles.restScreen}>
          <Timer size={32} color="#60a5fa" />
          <div style={styles.restTime}>
            {Math.floor(restSeconds / 60)}:{(restSeconds % 60).toString().padStart(2, '0')}
          </div>
          <p style={styles.restLabel}>Rest</p>
          <p style={styles.restNext}>
            Next: Set {(currentDrill?.completed_sets || 0) + 1} of {currentDrill?.target_sets || 1}
          </p>
          <button onClick={skipRest} style={styles.skipRestBtn}>
            Skip Rest
          </button>
        </div>
      </div>
    );
  }

  if (!currentDrill) {
    return (
      <div style={styles.container}>
        <p>No exercises found.</p>
        <button onClick={onClose} style={styles.closeBtn}>Close</button>
      </div>
    );
  }

  const isDurationBased = currentDrill.target_duration_seconds && !currentDrill.target_reps;

  // Get prev/next drill names for navigation
  const prevDrill = currentIndex > 0 ? drills[currentIndex - 1] : null;
  const nextDrill = currentIndex < drills.length - 1 ? drills[currentIndex + 1] : null;

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <button onClick={onClose} style={styles.headerBtn}>
          <X size={24} />
        </button>
        <div style={styles.progressContainer}>
          <div style={{ ...styles.progressBar, width: `${progress}%` }} />
        </div>
        <span style={styles.progressText}>{currentIndex + 1}/{drills.length}</span>
      </div>

      {/* Main content */}
      <div style={styles.main}>
        {/* Category & Difficulty badges */}
        {(currentDrill.category || currentDrill.difficulty_level) && (
          <div style={styles.badgeRow}>
            {currentDrill.category && (
              <Badge
                variant="info"
                size="sm"
                icon={Dumbbell}
                style={{
                  background: 'var(--accent-bg-light)',
                  color: 'var(--accent-light)',
                  border: '1px solid var(--accent-border-light)'
                }}
              >
                {currentDrill.category}
              </Badge>
            )}
            {currentDrill.difficulty_level && (
              <Badge
                variant={
                  currentDrill.difficulty_level === 'beginner' ? 'success' :
                  currentDrill.difficulty_level === 'intermediate' ? 'warning' : 'danger'
                }
                size="sm"
                style={{
                  background: currentDrill.difficulty_level === 'beginner' ? 'var(--success-bg-light)' :
                    currentDrill.difficulty_level === 'intermediate' ? 'rgba(234, 179, 8, 0.15)' : 'rgba(239, 68, 68, 0.15)',
                  color: currentDrill.difficulty_level === 'beginner' ? 'var(--success)' :
                    currentDrill.difficulty_level === 'intermediate' ? 'var(--warning)' : 'var(--error)',
                  border: 'none',
                }}
              >
                {currentDrill.difficulty_level}
              </Badge>
            )}
          </div>
        )}

        {/* Exercise name */}
        <h1 style={styles.exerciseName}>{currentDrill.drill_name}</h1>

        {/* Proof required indicator */}
        {currentDrill.requires_proof && (
          <Badge
            variant="accent"
            size="sm"
            icon={Video}
            style={{
              background: 'var(--accent-bg-medium)',
              color: 'var(--accent-light)',
              border: '1px solid var(--accent-border-light)',
              marginBottom: '12px'
            }}
          >
            Video proof required
          </Badge>
        )}

        {/* Description / Instructions */}
        {currentDrill.description && (
          <p style={styles.description}>{currentDrill.description}</p>
        )}

        {/* Target summary - what to do */}
        <div style={styles.targetSummary}>
          {currentDrill.target_sets && currentDrill.target_sets > 1 ? (
            <span>{currentDrill.target_sets} sets</span>
          ) : null}
          {currentDrill.target_sets && currentDrill.target_reps ? <span> × </span> : null}
          {currentDrill.target_reps ? (
            <span>{currentDrill.target_reps} reps</span>
          ) : null}
          {isDurationBased && currentDrill.target_duration_seconds ? (
            <span>
              {Math.floor(currentDrill.target_duration_seconds / 60) > 0
                ? `${Math.floor(currentDrill.target_duration_seconds / 60)} min `
                : ''}
              {currentDrill.target_duration_seconds % 60 > 0
                ? `${currentDrill.target_duration_seconds % 60} sec`
                : ''}
            </span>
          ) : null}
          {currentDrill.target_rest_seconds && currentDrill.target_rest_seconds > 0 && (
            <span style={styles.restInfo}>
              • {currentDrill.target_rest_seconds}s rest
            </span>
          )}
        </div>

        {/* Proof requirement - show "Record Now" action for drills needing proof */}
        {currentDrill.requires_proof && (
          <div style={styles.proofIndicator}>
            {currentDrill.proof_submitted ? (
              <div style={styles.proofSubmittedBadge}>
                <Check size={14} />
                <span>Proof Submitted</span>
              </div>
            ) : (
              <button
                onClick={() => {
                  setProofDrillIndex(currentDrill.drill_index);
                  setProofDrillName(currentDrill.drill_name);
                  setShowProofModal(true);
                }}
                style={styles.recordNowCard}
              >
                <div style={styles.recordNowIcon}>
                  <Camera size={20} />
                </div>
                <div style={styles.recordNowText}>
                  <span style={styles.recordNowTitle}>Record Now</span>
                  <span style={styles.recordNowSubtitle}>Set up and start recording before you begin</span>
                </div>
              </button>
            )}
          </div>
        )}

        {/* Set indicator */}
        <div style={styles.setIndicator}>
          <div style={styles.setDots}>
            {Array.from({ length: currentDrill.target_sets || 1 }).map((_, i) => (
              <div
                key={i}
                style={{
                  ...styles.setDot,
                  backgroundColor: i < currentDrill.completed_sets ? 'var(--accent, #f97316)' :
                    i === currentDrill.completed_sets ? 'var(--primary, #8b5cf6)' : 'var(--gray-700, #374151)',
                }}
              />
            ))}
          </div>
          <span style={styles.setCount}>
            Set {currentDrill.completed_sets + 1} of {currentDrill.target_sets || 1}
          </span>
        </div>

        {/* Reps/Duration display */}
        {isDurationBased ? (
          <div style={styles.durationDisplay}>
            <span style={styles.durationValue}>
              {Math.floor((currentDrill.target_duration_seconds || 0) / 60)}:
              {((currentDrill.target_duration_seconds || 0) % 60).toString().padStart(2, '0')}
            </span>
          </div>
        ) : (
          <div style={styles.repsControl}>
            <button
              onClick={() => adjustReps(-1)}
              style={{
                ...styles.repsBtn,
                opacity: currentDrill.reps_per_set <= 1 ? 0.3 : 1,
              }}
              disabled={currentDrill.reps_per_set <= 1}
            >
              <Minus size={36} strokeWidth={3} />
            </button>
            <div style={styles.repsDisplay}>
              <span style={styles.repsValue}>{currentDrill.reps_per_set}</span>
              <span style={styles.repsLabel}>REPS</span>
            </div>
            <button onClick={() => adjustReps(1)} style={styles.repsBtn}>
              <Plus size={36} strokeWidth={3} />
            </button>
          </div>
        )}
      </div>

      {/* Bottom actions */}
      <div style={styles.actions}>
        {/* Prev/Next navigation with exercise names */}
        <div style={styles.navRow}>
          <button
            onClick={() => setCurrentIndex(currentIndex - 1)}
            disabled={!prevDrill}
            style={{
              ...styles.navBtnWide,
              opacity: prevDrill ? 1 : 0.3,
            }}
          >
            <ChevronLeft size={20} />
            <span style={styles.navBtnText}>
              {prevDrill ? prevDrill.drill_name.substring(0, 12) + (prevDrill.drill_name.length > 12 ? '...' : '') : 'Prev'}
            </span>
          </button>

          <button
            onClick={skipDrill}
            disabled={saving}
            style={styles.skipBtn}
          >
            <SkipForward size={18} />
            <span>Skip</span>
          </button>

          <button
            onClick={() => setCurrentIndex(currentIndex + 1)}
            disabled={!nextDrill}
            style={{
              ...styles.navBtnWide,
              opacity: nextDrill ? 1 : 0.3,
            }}
          >
            <span style={styles.navBtnText}>
              {nextDrill ? nextDrill.drill_name.substring(0, 12) + (nextDrill.drill_name.length > 12 ? '...' : '') : 'Next'}
            </span>
            <ChevronRight size={20} />
          </button>
        </div>

        {/* Complete set button */}
        <button
          onClick={completeSet}
          disabled={saving}
          style={styles.completeSetBtn}
        >
          <Check size={32} strokeWidth={3} />
          <span>
            {currentDrill.completed_sets + 1 >= (currentDrill.target_sets || 1)
              ? 'DONE'
              : 'SET COMPLETE'}
          </span>
        </button>
      </div>

      {/* Skip Reason Modal */}
      {showSkipReasonModal && (
        <div style={styles.modalOverlay}>
          <div style={styles.skipReasonModal}>
            <div style={styles.skipReasonHeader}>
              <AlertCircle size={24} color="#f59e0b" />
              <h3 style={styles.skipReasonTitle}>Why are you skipping?</h3>
            </div>
            <p style={styles.skipReasonSubtitle}>
              This helps your coach adjust your training
            </p>
            <div style={styles.skipReasonGrid}>
              {SKIP_REASON_OPTIONS.map(option => (
                <button
                  key={option.value}
                  onClick={() => handleSkipWithReason(option.value)}
                  style={styles.skipReasonBtn}
                  disabled={saving}
                >
                  <span style={styles.skipReasonIcon}>{option.icon}</span>
                  <span style={styles.skipReasonLabel}>{option.label}</span>
                </button>
              ))}
            </div>
            <button
              onClick={() => {
                setShowSkipReasonModal(false);
                setPendingSkipIndex(null);
              }}
              style={styles.skipReasonCancelBtn}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Proof Prompt Screen - shown after completing drill that requires proof */}
      {showProofPrompt && completedDrillForProof && (
        <div style={styles.proofPromptOverlay}>
          <div style={styles.proofPromptContent}>
            {/* Success check icon */}
            <div style={styles.proofPromptIcon}>
              <Check size={40} color="#22c55e" strokeWidth={3} />
            </div>

            <h2 style={styles.proofPromptTitle}>
              Great job!
            </h2>
            <p style={styles.proofPromptDrillName}>
              {completedDrillForProof.drill_name}
            </p>

            {/* Proof request section */}
            <div style={styles.proofPromptBox}>
              <Video size={28} color="#60a5fa" />
              <div style={styles.proofPromptBoxText}>
                <p style={styles.proofPromptBoxTitle}>Video Proof Required</p>
                <p style={styles.proofPromptBoxSubtitle}>
                  Record or upload a video to verify your form
                </p>
              </div>
            </div>

            {/* Action buttons */}
            <button
              onClick={openProofModalFromPrompt}
              style={styles.proofPromptUploadBtn}
            >
              <Upload size={20} />
              <span>Upload Proof Video</span>
            </button>

            <button
              onClick={skipProofAndAdvance}
              style={styles.proofPromptSkipBtn}
            >
              Skip for now
            </button>
            <p style={styles.proofPromptSkipNote}>
              You can submit proof later from your programs
            </p>
          </div>
        </div>
      )}

      {/* Proof Upload Modal */}
      {showProofModal && proofDrillIndex !== null && (
        <ProofUploadModal
          programId={programId}
          moduleId={module.module_id}
          drillIndex={proofDrillIndex}
          drillName={proofDrillName}
          orgId={orgId}
          proxyAthleteId={proxyAthleteId}
          onClose={() => {
            setShowProofModal(false);
            setProofDrillIndex(null);
            setProofDrillName('');
            // If we were in the prompt flow and closed without submitting,
            // keep the prompt visible
          }}
          onSubmitted={handleProofSubmitted}
        />
      )}
    </div>
  );
};

// Check for small screen - will be used for responsive sizing
const isSmallScreen = typeof window !== 'undefined' && window.innerWidth < 400;

const styles: Record<string, React.CSSProperties> = {
  container: {
    backgroundColor: 'var(--primary-dark, #0c0a1d)',
    minHeight: '100vh',
    color: 'white',
    display: 'flex',
    flexDirection: 'column',
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 'var(--z-modal, 1000)' as unknown as number,
    overflow: 'auto',
  },
  loadingContainer: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
  },
  loadingText: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '16px',
    opacity: 0.7,
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '12px 16px',
    borderBottom: '1px solid var(--gray-800, #1f2937)',
    background: 'rgba(0, 0, 0, 0.2)',
  },
  headerBtn: {
    background: 'none',
    border: 'none',
    color: 'var(--gray-400, #9ca3af)',
    cursor: 'pointer',
    padding: '6px',
    borderRadius: 'var(--radius-sm, 6px)',
    transition: 'var(--transition-fast, 150ms ease)',
  },
  progressContainer: {
    flex: 1,
    height: '6px',
    backgroundColor: 'var(--gray-700, #374151)',
    borderRadius: 'var(--radius-sm, 3px)',
    overflow: 'hidden',
  },
  progressBar: {
    height: '100%',
    backgroundColor: 'var(--success, #F97316)',
    borderRadius: 'var(--radius-sm, 3px)',
    transition: 'width 0.3s ease',
    boxShadow: '0 0 8px var(--success-shadow, rgba(249, 115, 22, 0.4))',
  },
  progressText: {
    fontSize: 'var(--text-base, 14px)',
    fontWeight: '600',
    color: 'var(--gray-400, #9ca3af)',
    minWidth: '40px',
    textAlign: 'right',
  },
  main: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 'var(--space-7, 16px)',
    gap: 'var(--space-7, 16px)',
  },
  badgeRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 'var(--space-2, 6px)',
    marginBottom: 'var(--space-2, 6px)',
    justifyContent: 'center',
  },
  exerciseName: {
    fontSize: 'clamp(22px, 6vw, 32px)',
    fontWeight: '700',
    textAlign: 'center',
    margin: 0,
    lineHeight: 1.2,
    maxWidth: '100%',
    padding: '0 12px',
    wordBreak: 'break-word',
  },
  description: {
    fontSize: '14px',
    color: '#d1d5db',
    textAlign: 'center',
    lineHeight: 1.5,
    maxWidth: '100%',
    margin: '0 auto',
    padding: '0 12px',
  },
  targetSummary: {
    fontSize: '15px',
    color: '#9ca3af',
    textAlign: 'center',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '4px',
    flexWrap: 'wrap',
  },
  restInfo: {
    color: '#60a5fa',
    marginLeft: '6px',
  },
  setIndicator: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '10px',
    marginTop: '6px',
  },
  setDots: {
    display: 'flex',
    gap: '8px',
    flexWrap: 'wrap',
    justifyContent: 'center',
  },
  setDot: {
    width: '12px',
    height: '12px',
    borderRadius: '50%',
    transition: 'background-color 0.3s',
  },
  setCount: {
    fontSize: '14px',
    color: '#9ca3af',
    fontWeight: '500',
  },
  repsControl: {
    display: 'flex',
    alignItems: 'center',
    gap: 'clamp(16px, 5vw, 32px)',
    marginTop: '16px',
  },
  repsBtn: {
    width: 'clamp(56px, 18vw, 80px)',
    height: 'clamp(56px, 18vw, 80px)',
    borderRadius: '50%',
    backgroundColor: '#1f2937',
    border: '2px solid #374151',
    color: 'white',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    transition: 'all 0.15s',
    padding: 0,
  },
  repsDisplay: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    minWidth: '80px',
  },
  repsValue: {
    fontSize: 'clamp(56px, 20vw, 96px)',
    fontWeight: '700',
    lineHeight: 1,
  },
  repsLabel: {
    fontSize: '16px',
    color: '#6b7280',
    marginTop: '6px',
    letterSpacing: '2px',
    fontWeight: '600',
  },
  durationDisplay: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    marginTop: '16px',
  },
  durationValue: {
    fontSize: 'clamp(48px, 16vw, 72px)',
    fontWeight: '700',
    fontFamily: 'monospace',
  },
  actions: {
    padding: '12px 16px 24px',
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  navRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 'var(--space-2, 6px)',
  },
  navBtnWide: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '2px',
    padding: '10px 8px',
    backgroundColor: 'var(--gray-800, #1f2937)',
    border: '2px solid var(--gray-700, #374151)',
    borderRadius: 'var(--radius-md, 10px)',
    color: 'white',
    fontSize: 'var(--text-xs, 12px)',
    fontWeight: '500',
    cursor: 'pointer',
    minWidth: 0,
    transition: 'var(--transition-fast, 150ms ease)',
  },
  navBtnText: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    maxWidth: '60px',
  },
  skipBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    padding: '10px 12px',
    backgroundColor: 'transparent',
    border: '2px solid var(--gray-700, #374151)',
    borderRadius: 'var(--radius-md, 10px)',
    color: 'var(--gray-400, #9ca3af)',
    fontSize: 'var(--text-xs, 12px)',
    fontWeight: '600',
    cursor: 'pointer',
    flexShrink: 0,
    transition: 'var(--transition-fast, 150ms ease)',
  },
  completeSetBtn: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '10px',
    padding: '18px',
    backgroundColor: 'var(--success, #F97316)',
    border: 'none',
    borderRadius: 'var(--radius-lg, 14px)',
    color: 'white',
    fontSize: 'var(--text-xl, 18px)',
    fontWeight: '800',
    cursor: 'pointer',
    boxShadow: '0 4px 20px var(--success-shadow, rgba(249, 115, 22, 0.4))',
    letterSpacing: '1px',
    transition: 'var(--transition-fast, 150ms ease)',
  },
  // Rest screen
  restScreen: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 'var(--space-5, 12px)',
    padding: 'var(--space-7, 16px)',
  },
  restTime: {
    fontSize: 'clamp(64px, 22vw, 96px)',
    fontWeight: '700',
    fontFamily: 'monospace',
    color: 'var(--accent-light, #A78BFA)',
  },
  restLabel: {
    fontSize: 'var(--text-2xl, 20px)',
    color: 'var(--gray-500, #6b7280)',
    textTransform: 'uppercase',
    letterSpacing: '3px',
  },
  restNext: {
    fontSize: 'var(--text-base, 14px)',
    color: 'var(--gray-400, #9ca3af)',
    marginTop: 'var(--space-7, 16px)',
    textAlign: 'center',
  },
  skipRestBtn: {
    marginTop: 'var(--space-9, 24px)',
    padding: '14px 28px',
    backgroundColor: 'var(--gray-800, #1f2937)',
    border: '1px solid var(--gray-700, #374151)',
    borderRadius: 'var(--radius-md, 10px)',
    color: 'white',
    fontSize: 'var(--text-base, 14px)',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'var(--transition-fast, 150ms ease)',
  },
  // Complete screen
  completeScreen: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 'var(--space-7, 16px)',
    gap: 'var(--space-5, 12px)',
  },
  trophyCircle: {
    width: '80px',
    height: '80px',
    borderRadius: '50%',
    backgroundColor: 'var(--success-bg-light, rgba(249, 115, 22, 0.1))',
    border: '2px solid var(--success, #F97316)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 'var(--space-2, 6px)',
    boxShadow: '0 0 20px var(--success-shadow, rgba(249, 115, 22, 0.25))',
  },
  completeTitle: {
    fontSize: 'var(--text-3xl, 24px)',
    fontWeight: '700',
    margin: 0,
    textAlign: 'center',
    background: 'linear-gradient(135deg, var(--success, #F97316), var(--accent-light, #A78BFA))',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    backgroundClip: 'text',
  },
  completeSubtitle: {
    fontSize: 'var(--text-base, 14px)',
    color: 'var(--gray-400, #9ca3af)',
    margin: 0,
    textAlign: 'center',
  },
  difficultySection: {
    width: '100%',
    maxWidth: '300px',
    marginTop: 'var(--space-9, 24px)',
  },
  difficultyLabel: {
    fontSize: 'var(--text-base, 14px)',
    color: 'var(--gray-400, #9ca3af)',
    textAlign: 'center',
    marginBottom: 'var(--space-5, 12px)',
  },
  difficultyGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: 'var(--space-4, 10px)',
  },
  difficultyBtn: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 'var(--space-2, 6px)',
    padding: 'var(--space-5, 12px)',
    backgroundColor: 'var(--gray-800, #1f2937)',
    border: '2px solid var(--gray-700, #374151)',
    borderRadius: 'var(--radius-md, 10px)',
    color: 'white',
    cursor: 'pointer',
    transition: 'var(--transition-fast, 150ms ease)',
  },
  difficultyBtnActive: {
    borderColor: 'var(--success, #F97316)',
    backgroundColor: 'var(--success-bg-light, rgba(249, 115, 22, 0.1))',
  },
  difficultyEmoji: {
    fontSize: '28px',
  },
  difficultyText: {
    fontSize: 'var(--text-xs, 12px)',
    fontWeight: '500',
  },
  finishBtn: {
    marginTop: 'var(--space-9, 24px)',
    padding: '14px 40px',
    backgroundColor: 'var(--success, #F97316)',
    border: 'none',
    borderRadius: 'var(--radius-md, 10px)',
    color: 'white',
    fontSize: 'var(--text-lg, 16px)',
    fontWeight: '700',
    cursor: 'pointer',
    boxShadow: '0 4px 20px var(--success-shadow, rgba(249, 115, 22, 0.4))',
    transition: 'var(--transition-fast, 150ms ease)',
  },
  closeBtn: {
    padding: '10px 20px',
    backgroundColor: 'var(--gray-700, #374151)',
    border: 'none',
    borderRadius: 'var(--radius-sm, 8px)',
    color: 'white',
    fontSize: 'var(--text-base, 14px)',
    cursor: 'pointer',
    transition: 'var(--transition-fast, 150ms ease)',
  },
  // Proof indicator badge styles (informational only)
  proofIndicator: {
    marginTop: '8px',
    display: 'flex',
    justifyContent: 'center',
  },
  proofSubmittedBadge: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '6px 12px',
    backgroundColor: 'rgba(34, 197, 94, 0.1)',
    borderRadius: '20px',
    color: '#22c55e',
    fontSize: '12px',
    fontWeight: '500',
  },
  proofRequiredBadge: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '6px 12px',
    backgroundColor: 'rgba(96, 165, 250, 0.1)',
    borderRadius: '20px',
    color: '#60a5fa',
    fontSize: '12px',
    fontWeight: '500',
  },
  recordNowCard: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '14px 18px',
    backgroundColor: 'rgba(239, 68, 68, 0.1)',
    border: '2px solid rgba(239, 68, 68, 0.3)',
    borderRadius: '12px',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    width: '100%',
    maxWidth: '320px',
  },
  recordNowIcon: {
    width: '44px',
    height: '44px',
    borderRadius: '50%',
    backgroundColor: '#ef4444',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'white',
    flexShrink: 0,
  },
  recordNowText: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
    textAlign: 'left',
  },
  recordNowTitle: {
    fontSize: '15px',
    fontWeight: '700',
    color: '#ef4444',
  },
  recordNowSubtitle: {
    fontSize: '12px',
    color: '#9ca3af',
  },
  // Proof prompt screen styles (shown after completing drill)
  proofPromptOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: '#111827',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '24px',
    zIndex: 1100,
  },
  proofPromptContent: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
    maxWidth: '340px',
    width: '100%',
  },
  proofPromptIcon: {
    width: '80px',
    height: '80px',
    borderRadius: '50%',
    backgroundColor: 'rgba(34, 197, 94, 0.15)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: '20px',
  },
  proofPromptTitle: {
    fontSize: '24px',
    fontWeight: '700',
    color: 'white',
    margin: 0,
  },
  proofPromptDrillName: {
    fontSize: '16px',
    color: '#9ca3af',
    margin: '8px 0 24px',
  },
  proofPromptBox: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    padding: '16px 20px',
    backgroundColor: '#1f2937',
    borderRadius: '12px',
    border: '2px solid #374151',
    width: '100%',
    marginBottom: '24px',
  },
  proofPromptBoxText: {
    textAlign: 'left',
  },
  proofPromptBoxTitle: {
    fontSize: '15px',
    fontWeight: '600',
    color: 'white',
    margin: 0,
  },
  proofPromptBoxSubtitle: {
    fontSize: '13px',
    color: '#9ca3af',
    margin: '4px 0 0',
  },
  proofPromptUploadBtn: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '10px',
    width: '100%',
    padding: '16px 24px',
    backgroundColor: '#3b82f6',
    border: 'none',
    borderRadius: '12px',
    color: 'white',
    fontSize: '16px',
    fontWeight: '700',
    cursor: 'pointer',
    marginBottom: '12px',
  },
  proofPromptSkipBtn: {
    padding: '12px 24px',
    backgroundColor: 'transparent',
    border: '1px solid #374151',
    borderRadius: '10px',
    color: '#9ca3af',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
  },
  proofPromptSkipNote: {
    fontSize: '12px',
    color: '#6b7280',
    marginTop: '12px',
  },
  // Skip reason modal styles
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.8)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '16px',
    zIndex: 1100,
  },
  skipReasonModal: {
    backgroundColor: '#1f2937',
    borderRadius: '16px',
    padding: '24px',
    width: '100%',
    maxWidth: '360px',
    maxHeight: '90vh',
    overflow: 'auto',
  },
  skipReasonHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    marginBottom: '8px',
  },
  skipReasonTitle: {
    fontSize: '18px',
    fontWeight: '700',
    margin: 0,
    color: 'white',
  },
  skipReasonSubtitle: {
    fontSize: '13px',
    color: '#9ca3af',
    marginBottom: '20px',
  },
  skipReasonGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: '10px',
    marginBottom: '16px',
  },
  skipReasonBtn: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '6px',
    padding: '14px 10px',
    backgroundColor: '#374151',
    border: '2px solid #4b5563',
    borderRadius: '10px',
    color: 'white',
    fontSize: '12px',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'all 0.15s',
  },
  skipReasonIcon: {
    fontSize: '24px',
  },
  skipReasonLabel: {
    textAlign: 'center',
    lineHeight: 1.3,
  },
  skipReasonCancelBtn: {
    width: '100%',
    padding: '12px',
    backgroundColor: 'transparent',
    border: '1px solid #4b5563',
    borderRadius: '10px',
    color: '#9ca3af',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
  },
};

export default WorkoutTracker;
